import Input from '@/components/Input';
import Select from '@/components/Select';
import { useState, useEffect, useDeferredValue } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/Button';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdClear, MdCancel } from 'react-icons/md';
import type { SearchQuery as SharedSearchQuery } from '@/features/agent/shared_job_posts/hooks/useSharedJobPosts';
import { useQuery } from '@tanstack/react-query';
import type { AgentApiSharedJobPostsMetadata } from '@/types/serializers';
import { ORDER_ITEMS, PREFECTURES } from '@/constants/sharedJobPostConstants';
import useSharedJobPostLists from '@/features/agent/shared_job_posts/hooks/useSharedJobPostLists';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tag } from '@/components/Tag';
import Checkbox from '@/components/Checkbox';

interface Props {
  searchQuery: SharedSearchQuery;
  onSearchQueryChange: (query: SharedSearchQuery) => void;
}

interface MultiSelectStringFieldProps {
  values: string[];
  options: string[];
  placeholder: string;
  onUpdate: (values: string[]) => void;
  searchable?: boolean;
  align?: 'start' | 'end';
}

function MultiSelectStringField({
  values,
  options,
  placeholder,
  onUpdate,
  searchable = false,
  align = 'start',
}: MultiSelectStringFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const selectedValues = values.filter(Boolean);

  const filteredOptions = searchable
    ? options.filter((option) => option.toLowerCase().includes(deferredQuery.toLowerCase()))
    : options;

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const newValues = [...values];
    const index = newValues.indexOf(option);

    if (checked && index === -1) {
      newValues.push(option);
    } else if (!checked && index !== -1) {
      newValues.splice(index, 1);
    }

    onUpdate(newValues);
  };

  const removeValue = (valueToRemove: string) => {
    const newValues = values.filter((value) => value !== valueToRemove);
    onUpdate(newValues);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex h-xxl w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white p-2">
          <div className="flex flex-1 gap-2 overflow-x-auto pr-2 text-nowrap">
            {selectedValues.length > 0 ? (
              selectedValues.map((value) => (
                <div key={value} className="shrink-0">
                  <Tag color="default" className="flex items-center gap-1">
                    {value}
                    <button
                      type="button"
                      className="flex size-5 items-center justify-center rounded-full border-none bg-transparent p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(value);
                      }}
                    >
                      <MdCancel className="fill-main text-lg opacity-50" />
                    </button>
                  </Tag>
                </div>
              ))
            ) : (
              <span className="text-placeholder">{placeholder}</span>
            )}
          </div>
          {selectedValues.length > 0 && (
            <div className="pointer-events-none absolute inset-y-2 right-8 w-8 bg-gradient-to-l from-white to-transparent" />
          )}
          <MdKeyboardArrowDown
            className={`text-xl text-main transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align={align} className="z-[110] w-[724px] p-4">
        <div className="space-y-4">
          {searchable && (
            <Input
              search
              className="w-full"
              placeholder="フリーワードで検索"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          <div className="max-h-[264px] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              {filteredOptions.map((option) => (
                <Checkbox
                  key={option}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(checked) => handleCheckboxChange(option, checked)}
                >
                  <span className="font-normal">{option}</span>
                </Checkbox>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CandidatesSharedJobPostModalSearchForm({
  searchQuery,
  onSearchQueryChange,
}: Props) {
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // 企業名は入力途中の部分文字列でリクエストが乱発される（=サーバ負荷 / キャッシュへのノイズ蓄積）のを避けるため、
  // ローカル state で即時に表示しつつ、実際の検索クエリ反映はデバウンス（500ms）する。
  const companyNameDebounce = useDebounce(500);
  const [companyNameInput, setCompanyNameInput] = useState(searchQuery.company_name || '');

  // クリアボタン等で外部から company_name が変わった場合にローカル入力を同期する。
  useEffect(() => {
    setCompanyNameInput(searchQuery.company_name || '');
  }, [searchQuery.company_name]);

  // プロバイダーメタデータの取得
  const { data: metadata } = useQuery<AgentApiSharedJobPostsMetadata>({
    queryKey: ['/agent/api/shared_job_posts/metadata'],
    queryFn: async () => {
      const response = await fetch('/agent/api/shared_job_posts/metadata.json');
      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }
      return response.json();
    },
  });

  // リスト一覧の取得
  const { data: listsData } = useSharedJobPostLists();

  const providerDisplayNameMap = metadata?.available_providers
    ? Object.fromEntries(metadata.available_providers.map((item) => [item.key, item.label]))
    : {};

  // プロバイダー選択肢の生成
  const providerItems = metadata?.permitted_providers
    ? [
        { value: 'all', label: 'すべて' },
        ...metadata.permitted_providers.map((provider) => ({
          value: provider,
          label: providerDisplayNameMap[provider] || provider,
        })),
      ]
    : [{ value: 'loading', label: '読み込み中...' }];

  const updateQuery = (updates: Partial<SharedSearchQuery>) => {
    onSearchQueryChange({ ...searchQuery, ...updates });
  };

  const prefectureCodes = searchQuery.prefecture_codes ?? [];
  const prefectureNames = prefectureCodes
    .map((code) => PREFECTURES.find((p) => p.code === code)?.name)
    .filter((name): name is string => Boolean(name));

  const handlePrefectureUpdate = (names: string[]) => {
    const codes = names
      .map((name) => PREFECTURES.find((p) => p.name === name)?.code)
      .filter((code): code is number => code !== undefined);
    updateQuery({ prefecture_codes: codes });
  };

  // 検索条件をクリア
  const handleClearSearch = () => {
    onSearchQueryChange({
      search_query: '',
      company_name: '',
      job_position_name: '',
      min_salary: '',
      max_salary: '',
      salary: '',
      min_fee_rate: '',
      max_fee_rate: '',
      fee_rate: '',
      min_fee_value: '',
      max_fee_value: '',
      fee_value: '',
      sort: 'updated_at:desc',
      provider: undefined,
      shared_job_post_list_id: undefined,
      prefecture_codes: [],
    });
  };

  // 検索条件が設定されているかチェック
  const hasSearchConditions = () => {
    if (searchQuery.search_query && searchQuery.search_query.trim() !== '') return true;
    if (searchQuery.company_name && searchQuery.company_name.trim() !== '') return true;
    if (searchQuery.salary && searchQuery.salary.trim() !== '') return true;
    if (searchQuery.min_salary && searchQuery.min_salary.trim() !== '') return true;
    if (searchQuery.max_salary && searchQuery.max_salary.trim() !== '') return true;
    if (searchQuery.min_fee_rate && searchQuery.min_fee_rate.trim() !== '') return true;
    if (searchQuery.max_fee_rate && searchQuery.max_fee_rate.trim() !== '') return true;
    if (searchQuery.fee_rate && searchQuery.fee_rate.trim() !== '') return true;
    if (searchQuery.min_fee_value && searchQuery.min_fee_value.trim() !== '') return true;
    if (searchQuery.max_fee_value && searchQuery.max_fee_value.trim() !== '') return true;
    if (searchQuery.fee_value && searchQuery.fee_value.trim() !== '') return true;
    if (searchQuery.job_position_name && searchQuery.job_position_name.trim() !== '') return true;
    if (searchQuery.prefecture_codes && searchQuery.prefecture_codes.length > 0) return true;
    if (searchQuery.provider && searchQuery.provider.trim() !== '') return true;
    if (searchQuery.shared_job_post_list_id && searchQuery.shared_job_post_list_id.trim() !== '')
      return true;
    return false;
  };

  return (
    <>
      <div className="w-full rounded bg-gray p-md">
        <div className="space-y-lg">
          {/* 1段目: フリーワード, 企業名, 年収 */}
          <div className="flex gap-sm">
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">フリーワード</label>
              <Input
                search
                placeholder="フリーワードで絞り込む"
                value={searchQuery.search_query || ''}
                onChange={(e) => updateQuery({ search_query: e.target.value })}
                className="w-full"
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">企業名</label>
              <Input
                placeholder="企業名を入力して検索"
                value={companyNameInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setCompanyNameInput(value);
                  companyNameDebounce(() => updateQuery({ company_name: value }));
                }}
                className="w-full"
              />
            </div>

            <div className="flex shrink-0 flex-col gap-xs">
              <label className="text-heading-xxs">年収</label>
              <div className="flex items-center gap-xs whitespace-nowrap">
                <Input
                  placeholder="0"
                  value={searchQuery.salary || ''}
                  onChange={(e) => updateQuery({ salary: e.target.value })}
                  className="w-[96px]"
                />
                <span className="text-body-sm">万円を含む</span>
                <Input
                  placeholder="0"
                  value={searchQuery.min_salary || ''}
                  onChange={(e) => updateQuery({ min_salary: e.target.value })}
                  className="w-[96px]"
                />
                <span className="text-body-sm">〜</span>
                <Input
                  placeholder="0"
                  value={searchQuery.max_salary || ''}
                  onChange={(e) => updateQuery({ max_salary: e.target.value })}
                  className="w-[96px]"
                />
                <span className="text-body-sm">万円</span>
              </div>
            </div>
          </div>

          {/* 2段目: ポジション, 勤務地, リスト */}
          <div className="flex gap-sm">
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">ポジション</label>
              <Input
                search
                placeholder="ポジションで絞り込む"
                value={searchQuery.job_position_name || ''}
                onChange={(e) => updateQuery({ job_position_name: e.target.value })}
                className="w-full"
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-xs">
              <label className="text-heading-xxs">勤務地</label>
              <MultiSelectStringField
                values={prefectureNames}
                options={PREFECTURES.map((p) => p.name)}
                placeholder="勤務地を選択"
                onUpdate={handlePrefectureUpdate}
                searchable
                align="start"
              />
            </div>

            {listsData?.records && listsData.records.length > 0 && (
              <div className="flex min-w-0 flex-1 flex-col gap-xs">
                <label className="text-heading-xxs">リスト</label>
                <Select
                  className="w-full"
                  items={[
                    { value: 'all', label: 'すべて' },
                    ...listsData.records.map((list) => ({
                      value: String(list.id),
                      label: list.name,
                    })),
                  ]}
                  value={searchQuery.shared_job_post_list_id || 'all'}
                  onValueChange={(value) =>
                    updateQuery({ shared_job_post_list_id: value === 'all' ? undefined : value })
                  }
                />
              </div>
            )}
          </div>

          {/* 詳細検索アコーディオンボタンとクリアボタン */}
          <div className="flex items-center justify-between">
            <Button
              color="tertiary"
              size="md"
              type="button"
              onClick={() => setIsDetailOpen(!isDetailOpen)}
            >
              {isDetailOpen ? (
                <MdKeyboardArrowUp className="size-icon-xs text-primary" />
              ) : (
                <MdKeyboardArrowDown className="size-icon-xs text-primary" />
              )}
              <span className="text-sm font-bold text-primary">
                {isDetailOpen ? '詳細条件を閉じる' : '詳細条件を開く'}
              </span>
            </Button>

            {hasSearchConditions() && (
              <Button
                color="secondary"
                size="sm"
                type="button"
                onClick={handleClearSearch}
                className="flex items-center gap-1"
              >
                <MdClear className="size-icon-xs" />
                <span className="text-sm font-bold">検索条件をクリア</span>
              </Button>
            )}
          </div>

          {/* 詳細検索アコーディオンコンテンツ */}
          {isDetailOpen && (
            <div className="flex gap-sm">
              {metadata?.permitted_providers && metadata.permitted_providers.length > 0 && (
                <div className="flex min-w-0 flex-1 flex-col gap-xs">
                  <label className="text-heading-xxs">取り込み元</label>
                  <Select
                    className="w-full"
                    items={providerItems}
                    value={searchQuery.provider || 'all'}
                    onValueChange={(value) =>
                      updateQuery({ provider: value === 'all' ? undefined : value })
                    }
                  />
                </div>
              )}

              <div className="flex shrink-0 flex-col gap-xs">
                <label className="text-heading-xxs">成約料率</label>
                <div className="flex items-center gap-xs whitespace-nowrap">
                  <Input
                    placeholder="0"
                    value={searchQuery.min_fee_rate || ''}
                    onChange={(e) => updateQuery({ min_fee_rate: e.target.value })}
                    className="w-[96px]"
                  />
                  <span className="text-body-sm">%</span>
                  <span className="text-body-sm">〜</span>
                  <Input
                    placeholder="0"
                    value={searchQuery.max_fee_rate || ''}
                    onChange={(e) => updateQuery({ max_fee_rate: e.target.value })}
                    className="w-[96px]"
                  />
                  <span className="text-body-sm">%</span>
                </div>
              </div>

              <div className="flex shrink-0 flex-col gap-xs">
                <label className="text-heading-xxs">成約金額</label>
                <div className="flex items-center gap-xs whitespace-nowrap">
                  <Input
                    placeholder="0"
                    value={searchQuery.min_fee_value || ''}
                    onChange={(e) => updateQuery({ min_fee_value: e.target.value })}
                    className="w-[96px]"
                  />
                  <span className="text-body-sm">万円</span>
                  <span className="text-body-sm">〜</span>
                  <Input
                    placeholder="0"
                    value={searchQuery.max_fee_value || ''}
                    onChange={(e) => updateQuery({ max_fee_value: e.target.value })}
                    className="w-[96px]"
                  />
                  <span className="text-body-sm">万円</span>
                </div>
              </div>
            </div>
          )}

          {/* TODO: 検索カテゴリ縮小に伴い詳細キーワード設定を一時的に非表示 */}
        </div>
      </div>

      {/* ソート */}
      <div className="mt-sm flex w-[196px] shrink-0 flex-col gap-xs">
        <Select
          className="w-full"
          items={[...ORDER_ITEMS]}
          value={searchQuery.sort}
          onValueChange={(value) => updateQuery({ sort: value })}
        />
      </div>
    </>
  );
}
